import { works, type WorkItem } from "@/mocks/videos";
import { categories, type CategoryItem } from "@/mocks/categories";

export type { WorkItem, CategoryItem };

export const allWorks: WorkItem[] = works;
export const allCategories: CategoryItem[] = categories;

export function getCategory(key: string): CategoryItem | undefined {
  return categories.find((item) => item.key === key);
}

export function getWorkById(id: string): WorkItem | undefined {
  return works.find((item) => item.id === id);
}

export function matchCategory(work: WorkItem, type: string): boolean {
  switch (type) {
    case "drama":
      return work.type === "电视剧";
    case "movie":
      return work.type === "电影";
    case "classic":
      return work.categories.includes("classic");
    case "latest":
      return work.categories.includes("latest");
    default:
      return true;
  }
}

export function getWorksByCategory(type: string): WorkItem[] {
  return works.filter((item) => matchCategory(item, type));
}

export function getFeaturedWorks(): WorkItem[] {
  const ids = ["midnight-cafe", "spring-will-come", "mothers-notes", "after-the-rain", "old-love-letter"];
  return ids
    .map((id) => getWorkById(id))
    .filter((item): item is WorkItem => Boolean(item));
}

export function getLatestWorks(limit = 8): WorkItem[] {
  return [...works]
    .sort((a, b) => b.year - a.year)
    .slice(0, limit);
}

export function getPopularWorks(limit = 6): WorkItem[] {
  return [...works]
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function getRelatedWorks(work: WorkItem, limit = 6): WorkItem[] {
  const samePrimary = works.filter(
    (item) => item.id !== work.id && item.type === work.type,
  );
  const sameTag = works.filter(
    (item) =>
      item.id !== work.id &&
      item.type !== work.type &&
      item.tags.some((tag) => work.tags.includes(tag)),
  );
  const rest = works.filter(
    (item) =>
      item.id !== work.id &&
      !samePrimary.includes(item) &&
      !sameTag.includes(item),
  );
  return [...samePrimary, ...sameTag, ...rest].slice(0, limit);
}

export function searchWorks(query: string): WorkItem[] {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return [];
  return works.filter((item) => {
    const haystack = [
      item.title,
      item.originalTitle,
      item.genre,
      item.director,
      item.synopsis,
      item.description,
      item.type,
      ...item.tags,
      ...item.cast,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(keyword);
  });
}

export function availableYears(type: string): number[] {
  const list = getWorksByCategory(type).map((item) => item.year);
  return Array.from(new Set(list)).sort((a, b) => b - a);
}

export function filterWorks(
  type: string,
  filters: { media?: string; year?: string; minRating?: string },
): WorkItem[] {
  let list = getWorksByCategory(type);
  const { media, year, minRating } = filters;
  if (media && media !== "all") {
    list = list.filter((item) => item.type === media);
  }
  if (year && year !== "all") {
    list = list.filter((item) => String(item.year) === year);
  }
  if (minRating && minRating !== "all") {
    const min = Number(minRating);
    list = list.filter((item) => (item.rating ?? 0) >= min);
  }
  return list;
}

export type SortKey = "latest" | "popular" | "rating" | "year";

export function sortWorks(list: WorkItem[], sort: SortKey): WorkItem[] {
  const copy = [...list];
  switch (sort) {
    case "popular":
      return copy.sort((a, b) => b.views - a.views);
    case "rating":
      return copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    case "year":
      return copy.sort((a, b) => b.year - a.year);
    case "latest":
    default:
      return copy;
  }
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)} 万`;
  }
  return String(views);
}